import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Skeleton } from "@mui/material";
import {
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon
} from "@mui/icons-material";
import { green, red, orange } from "@mui/material/colors";
import {
  authenticateGoogleCalendar,
  authenticateOutlookCalendar,
  refreshToken,
  refreshOutlookToken,
  revokeToken,
  revokeOutlookToken
} from "../api/actions/mutation";
import { getCalendarSettings } from "../api/actions/query";

const Permission = ({ canEdit, canDelete, activeTab, onTabChange, selectedConsultationType = null, onSettingsUpdate = null, onBeforeAuthorize = null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authDetails, setAuthDetails] = useState(null);
  const provider = activeTab || 'google';
  const setProvider = onTabChange;
  const [allSettings, setAllSettings] = useState(null);

  const updateAuthStatus = (settings, currentProvider) => {
    let tokens = null;
    if (selectedConsultationType && selectedConsultationType._id !== 'default') {
      // Find latest tokens from settings or fallback to prop
      const customType = settings?.consultationTypes?.find(t => (t._id || t.id)?.toString() === (selectedConsultationType._id || selectedConsultationType.id)?.toString()) || selectedConsultationType;
      tokens = currentProvider === 'google' ? customType?.googleOauthTokens : customType?.outlookOauthTokens;
    } else {
      if (currentProvider === 'google') {
        tokens = settings?.googleConfig?.googleOauthTokens || settings?.defaultConsultationType?.googleOauthTokens || null;
      } else if (currentProvider === 'outlook') {
        tokens = settings?.outlookConfig?.outlookOauthTokens || settings?.defaultConsultationType?.outlookOauthTokens || null;
      }
    }

    setAuthDetails(tokens);

    if (tokens) {
      const hasToken = !!tokens.refresh_token || !!tokens.access_token;
      setIsAuthorized(hasToken);
    } else {
      setIsAuthorized(false);
    }
  };

  const fetchSettings = async () => {
    setIsCheckingAuth(true);
    try {
      const response = await getCalendarSettings();
      setAllSettings(response);

      // Synchronize tab with current provider initially if not already set
      if (response.calendarProvider && !activeTab) {
        setProvider(response.calendarProvider);
      } else if (activeTab === 'google' && response.isCalendarGoogleEnabled === false && response.isCalendarOutlookEnabled !== false) {
        setProvider('outlook');
      } else if (activeTab === 'outlook' && response.isCalendarOutlookEnabled === false && response.isCalendarGoogleEnabled !== false) {
        setProvider('google');
      }

      updateAuthStatus(response, activeTab || response.calendarProvider || 'google');
      return response;
    } catch (err) {
      setIsAuthorized(false);
      setAuthDetails(null);
    } finally {
      setIsCheckingAuth(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(false);

    const handleWindowFocus = () => {
      setIsLoading(false);
    };

    window.addEventListener('pageshow', handleWindowFocus);
    window.addEventListener('focus', handleWindowFocus);

    fetchSettings();

    return () => {
      window.removeEventListener('pageshow', handleWindowFocus);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [activeTab, selectedConsultationType]); // Re-fetch or re-evaluate when activeTab or selectedConsultationType changes

  const handleProviderChange = (event, newProvider) => {
    if (newProvider !== null) {
      setProvider(newProvider);
      if (allSettings) {
        updateAuthStatus(allSettings, newProvider);
      }
    }
  };

  const handleAuthorize = async () => {
    setIsLoading(true);
    try {
      const typeId = selectedConsultationType?._id || 'default';
      sessionStorage.setItem('oauth_consultation_type_id', typeId);

      let extraParams = {};
      if (typeof onBeforeAuthorize === 'function') {
        try {
          const res = await onBeforeAuthorize();
          if (res?.calendarEmail) {
            extraParams.calendarEmail = res.calendarEmail;
            sessionStorage.setItem('oauth_calendar_email', res.calendarEmail);
          }
        } catch (e) {
          console.warn("onBeforeAuthorize failed:", e);
        }
      }

      let response;
      if (provider === 'google') {
        response = await authenticateGoogleCalendar({ consultationTypeId: typeId, ...extraParams });
      } else {
        response = await authenticateOutlookCalendar({ consultationTypeId: typeId, ...extraParams });
      }
      window.location.href = response.authUrl;
    } catch (error) {
      console.error("Authorization failed:", error);
      setIsLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      let data;
      const consultationTypeId = selectedConsultationType?._id || 'default';
      if (provider === 'google') {
        data = await refreshToken({ consultationTypeId });
      } else {
        data = await refreshOutlookToken({ consultationTypeId });
      }

      if (data.success) {
        setAuthDetails(prev => ({
          ...prev,
          expiry_date: data.data,
        }));
        setIsAuthorized(true);
        if (allSettings) {
          const configField = provider === 'google' ? 'googleOauthTokens' : 'outlookOauthTokens';
          const providerConfigKey = provider === 'google' ? 'googleConfig' : 'outlookConfig';
          const updatedSettings = { ...allSettings };

          // Update root legacy field
          updatedSettings[configField] = {
            ...(allSettings[configField] || (provider === 'google' ? allSettings.oauthTokens : {})),
            expiry_date: data.data
          };

          if (selectedConsultationType && selectedConsultationType._id !== 'default') {
            const idx = updatedSettings.consultationTypes?.findIndex(t => (t._id || t.id)?.toString() === (selectedConsultationType._id || selectedConsultationType.id)?.toString());
            if (idx >= 0) {
              updatedSettings.consultationTypes[idx] = {
                ...updatedSettings.consultationTypes[idx],
                [configField]: {
                  ...(updatedSettings.consultationTypes[idx][configField] || {}),
                  expiry_date: data.data
                }
              };
            }
          } else {
            if (updatedSettings[providerConfigKey]) {
              updatedSettings[providerConfigKey] = {
                ...updatedSettings[providerConfigKey],
                [configField]: {
                  ...(updatedSettings[providerConfigKey][configField] || {}),
                  expiry_date: data.data
                }
              };
            }
            if (updatedSettings.defaultConsultationType) {
              updatedSettings.defaultConsultationType = {
                ...updatedSettings.defaultConsultationType,
                [configField]: {
                  ...(updatedSettings.defaultConsultationType[configField] || {}),
                  expiry_date: data.data
                }
              };
            }
          }
          setAllSettings(updatedSettings);
          if (typeof onSettingsUpdate === 'function') onSettingsUpdate(updatedSettings);
        }
        // Background re-fetch from database to confirm synchronization
        await fetchSettings();
      } else {
        alert(data?.message || "Token refresh failed. Google/Outlook authorization may have expired or been revoked. Please click Authorize to reconnect.");
        await fetchSettings();
      }
    } catch (err) {
      console.error("Failed to refresh token:", err);
      alert(err?.message || "Token refresh failed. Please click Authorize to reconnect.");
      await fetchSettings();
    }
  };

  const handleRemoveToken = async () => {
    try {
      const consultationTypeId = selectedConsultationType?._id || 'default';
      if (provider === 'google') {
        const data = await revokeToken({ consultationTypeId });
        if (data.success) {
          setAuthDetails(null);
          setIsAuthorized(false);
          if (allSettings) {
            const newSettings = { ...allSettings };
            if (selectedConsultationType && selectedConsultationType._id !== 'default') {
              const idx = newSettings.consultationTypes?.findIndex(t => t._id === selectedConsultationType._id);
              if (idx >= 0) {
                newSettings.consultationTypes[idx].googleOauthTokens = null;
                newSettings.consultationTypes[idx].calendarEmail = "";
              }
            } else {
              delete newSettings.googleOauthTokens;
              delete newSettings.oauthTokens;
              if (newSettings.defaultConsultationType) {
                newSettings.defaultConsultationType.googleOauthTokens = null;
                newSettings.defaultConsultationType.calendarEmail = "";
                newSettings.defaultConsultationType.calendarUrl = "";
              }
              if (newSettings.googleConfig) {
                newSettings.googleConfig.googleOauthTokens = null;
                newSettings.googleConfig.email = "";
                newSettings.googleConfig.calendarUrl = "";
              }
            }
            setAllSettings(newSettings);
            if (typeof onSettingsUpdate === 'function') onSettingsUpdate(newSettings);
          }
        }
      } else {
        const data = await revokeOutlookToken({ consultationTypeId });
        if (data.success) {
          setAuthDetails(null);
          setIsAuthorized(false);
          if (allSettings) {
            const newSettings = { ...allSettings };
            if (selectedConsultationType && selectedConsultationType._id !== 'default') {
              const idx = newSettings.consultationTypes?.findIndex(t => t._id === selectedConsultationType._id);
              if (idx >= 0) {
                newSettings.consultationTypes[idx].outlookOauthTokens = null;
                newSettings.consultationTypes[idx].calendarEmail = "";
              }
            } else {
              delete newSettings.outlookOauthTokens;
              if (newSettings.defaultConsultationType) {
                newSettings.defaultConsultationType.outlookOauthTokens = null;
                newSettings.defaultConsultationType.calendarEmail = "";
              }
              if (newSettings.outlookConfig) {
                newSettings.outlookConfig.outlookOauthTokens = null;
                newSettings.outlookConfig.email = "";
              }
            }
            setAllSettings(newSettings);
            if (typeof onSettingsUpdate === 'function') onSettingsUpdate(newSettings);
          }
        }
      }

    } catch (err) {
      console.error("Failed to revoking token:", err);
    }
  };

  const formatExpiryDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  const formatRefreshDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp - 15 * 60 * 1000);
    return date.toLocaleString();
  };

  const getStatusDetails = () => {
    if (isCheckingAuth) {
      return {
        text: "Checking authorization status...",
        severity: "info",
        color: "info.main",
        icon: <InfoIcon sx={{ color: orange[500] }} />
      };
    }
    if (isAuthorized) {
      return {
        text: `Connected to ${provider === 'google' ? 'Google' : 'Outlook'} Calendar API`,
        severity: "success",
        color: "success.main",
        icon: <CheckCircleIcon sx={{ color: green[500] }} />
      };
    }
    return {
      text: `Disconnected from ${provider === 'google' ? 'Google' : 'Outlook'} Calendar API`,
      severity: "error",
      color: "error.main",
      icon: <WarningIcon sx={{ color: red[500] }} />
    };
  };

  const status = getStatusDetails();
  return (
    <Box sx={{}}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Account Management
          </Typography>
          <ToggleButtonGroup
            value={provider}
            exclusive
            onChange={handleProviderChange}
            aria-label="text alignment"
            size="small"
          >
            {((allSettings?.isCalendarGoogleEnabled !== false && allSettings?.isCalendarGoogleEnabled !== "false") ?? true) && (
              <ToggleButton value="google" aria-label="google">
                Google
                {allSettings?.calendarProvider === 'google' && (
                  <Box component="span" sx={{ width: 8, height: 8, bgcolor: '#4caf50', borderRadius: '50%', ml: 1, boxShadow: '0 0 4px #4caf50' }} title="Active Provider" />
                )}
              </ToggleButton>
            )}
            {((allSettings?.isCalendarOutlookEnabled !== false && allSettings?.isCalendarOutlookEnabled !== "false") ?? true) && (
              <ToggleButton value="outlook" aria-label="outlook">
                Outlook
                {allSettings?.calendarProvider === 'outlook' && (
                  <Box component="span" sx={{ width: 8, height: 8, bgcolor: '#4caf50', borderRadius: '50%', ml: 1, boxShadow: '0 0 4px #4caf50' }} title="Active Provider" />
                )}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Stack>

        {/* ── Loading skeleton while initial fetch is in progress ── */}
        {isCheckingAuth ? (
          <Box sx={{ mt: 2 }}>
            <Skeleton
              variant="rounded"
              width="100%"
              height={56}
              sx={{ mb: 2, borderRadius: 1 }}
              animation="wave"
            />
            <Stack direction="row" spacing={1}>
              <Skeleton variant="rounded" width={100} height={34} animation="wave" />
              <Skeleton variant="rounded" width={120} height={34} animation="wave" />
            </Stack>
          </Box>
        ) : (
          <>
            {/* ── Connection status alert ── */}
            <Box sx={{ mt: 2, mb: 3 }}>
              {isAuthorized && authDetails ? (
                (() => {
                  const isExpired = authDetails.expiry_date ? (authDetails.expiry_date < Date.now()) : false;
                  return (
                    <Alert severity={isExpired ? "warning" : "info"} icon={isExpired ? <WarningIcon /> : status.icon}>
                      <Typography variant="body2" gutterBottom>
                        {isExpired
                          ? `Your ${provider === 'google' ? 'Google' : 'Outlook'} Calendar token has expired.`
                          : `Your ${provider === 'google' ? 'Google' : 'Outlook'} Calendar connection is active.`
                        }
                      </Typography>
                      {authDetails.expiry_date && (
                        <>
                          <Typography variant="caption" component="div">
                            <strong>Token expires:</strong> {formatExpiryDate(authDetails.expiry_date)}
                          </Typography>
                          {!isExpired && (
                            <Typography variant="caption" component="div">
                              <strong>Token refresh:</strong> {formatRefreshDate(authDetails.expiry_date)}
                            </Typography>
                          )}
                          {isExpired && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                              Please click "Authorize" or "Refresh Token" to re-link your calendar account.
                            </Typography>
                          )}
                        </>
                      )}
                    </Alert>
                  );
                })()
              ) : (
                <Alert severity="error" icon={status.icon}>
                  <Typography variant="body2" gutterBottom>
                    Your {provider === 'google' ? 'Google' : 'Outlook'} Calendar connection is not active.
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* ── Action buttons ── */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={isLoading ? null : <LockIcon />}
                onClick={handleAuthorize}
                disabled={isLoading || !canEdit}
                size="small"
                sx={{
                  backgroundColor: 'rgb(237, 108, 2)',
                  color: 'white',
                  textTransform: "none",
                  '&:hover': {
                    backgroundColor: 'rgba(237, 108, 2, 0.8)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(237, 108, 2, 0.5)',
                    color: 'white',
                  },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                    Authorizing...
                  </>
                ) : (
                  isAuthorized ? "Re-authorize" : "Authorize"
                )}
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshToken}
                disabled={!isAuthorized || isLoading || !canEdit}
                size="small"
                sx={{
                  color: '#0D3559',
                  borderColor: '#0D3559',
                  textTransform: "none",
                  '&:hover': {
                    borderColor: '#0D3559',
                    backgroundColor: 'rgba(13, 53, 89, 0.04)',
                  },
                }}
              >
                Refresh Token
              </Button>

              {isAuthorized && !!authDetails && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveToken}
                  disabled={!canDelete}
                  size="small"
                  sx={{
                    borderColor: red[500],
                    color: red[500],
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(211,47,47,0.04)",
                      borderColor: red[700],
                      color: red[700],
                    },
                  }}
                >
                  Remove Token
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Permission;
